/**
 * FE mirror of backend RequiredField for the application-answers sheet (#20).
 * Partial spike: only `screening` fields are in-scope for the fill UI.
 *
 * SELECT / MULTI_SELECT: options are not yet on NormalizedScreeningQuestion /
 * RequiredApplicationField — use text-input fallback in the sheet (v1).
 */

import type { RequiredApplicationField } from "@/lib/api/model";

export type ApplicationFieldSource = "screening" | "custom" | "form";

export interface ApplicationFieldDef {
  id: string;
  label: string;
  type: string;
  required: boolean;
  blankable: boolean;
  source: ApplicationFieldSource;
  /** EEO / demographic — never shown in the sheet. */
  sensitive?: boolean;
  /** Unsupported/unrenderable types (FILE, etc.) — never shown in the sheet. */
  renderable?: boolean;
  display_type?: string | null;
}

/** Map OpenAPI RequiredApplicationField → sheet def. */
export function fromApiFieldDefs(
  defs: RequiredApplicationField[] | undefined | null,
): ApplicationFieldDef[] {
  return (defs ?? []).map((d) => ({
    id: d.id,
    label: d.label,
    type: d.type,
    required: d.required,
    blankable: d.blankable,
    source: d.source,
    sensitive: d.sensitive,
    renderable: d.renderable,
    display_type: d.display_type,
  }));
}

/** Screening-only, non-EEO, renderable required fields for the fill sheet. */
export function toSheetFields(
  defs: ApplicationFieldDef[] | undefined | null,
): ApplicationFieldDef[] {
  return (defs ?? []).filter(
    (f) =>
      f.source === "screening" &&
      f.required &&
      !f.sensitive &&
      f.renderable !== false,
  );
}

export type DraftAnswer = {
  value: string;
  blank: boolean;
};

export function draftFromAnswers(
  fields: ApplicationFieldDef[],
  answers: Record<string, unknown> | null | undefined,
): Record<string, DraftAnswer> {
  const map =
    answers &&
    typeof answers === "object" &&
    answers.fields &&
    typeof answers.fields === "object"
      ? (answers.fields as Record<string, { value?: unknown; blank?: boolean }>)
      : {};
  const out: Record<string, DraftAnswer> = {};
  for (const f of fields) {
    const entry = map[f.id];
    out[f.id] = {
      value:
        entry?.value === null || entry?.value === undefined
          ? ""
          : String(entry.value),
      blank: entry?.blank === true,
    };
  }
  return out;
}
